import type {
	AllFilterStates,
	MinimalFilterValue,
} from '../../../../common/constants/filters/types';
import type { PrimaryProductKey } from '../../../../common/constants/products';
import { isForgeConnectorProductKey } from '../../../../common/constants/schemas/3p-config';
import {
	type ProductKeys,
	productKeySchema,
} from '../../../../common/constants/schemas/query-params';
import { SearchExperience, type SearchStoreExperience } from '../../../../common/constants/search';
import { notEmpty } from '../../../../common/utils/filters';
import { type QueryParams } from '../../../../common/utils/query-params/types';

export const getUrsProductKey = (primaryProduct?: PrimaryProductKey): 'confluence' | 'jira' => {
	// URS (User Recommendation Service) only supports 'confluence' and 'jira' as product keys.
	// Other values (e.g. 'townsquare', 'mercury') cause a 400 deserialization error.
	// Default to 'confluence' when the primary product is not one of these two.
	return primaryProduct === 'jira' || primaryProduct === 'confluence'
		? primaryProduct
		: 'confluence';
};

export const FilterUtils: (
	staticFilters: Record<string, MinimalFilterValue> | MinimalFilterValue[],
	filterStates: Record<string, AllFilterStates | undefined> | AllFilterStates[],
) => {
	staticFilters: Record<string, MinimalFilterValue>;
	staticFilterValues: MinimalFilterValue[];
	filterStates: Record<string, AllFilterStates | undefined>;
	filterStateValues: AllFilterStates[];
	getSelectedProducts: (queryParams: QueryParams) => string[];
	isMissingProductSelection: (queryParams: QueryParams) => boolean;
	filtersByProduct: (
		products: ProductKeys[],
		experience: SearchStoreExperience,
		primaryProduct?: PrimaryProductKey,
	) => any;
	getFiltersWithSelection: () => (
		| 'archived'
		| 'assignee'
		| 'reporter'
		| 'commentedByMe'
		| 'content'
		| 'contributor'
		| 'createdBy'
		| 'lastUpdated'
		| 'project'
		| 'space'
		| 'verified'
		| 'onlyTitleMatch'
		| 'universalTypeFilter'
		| 'focusAreaType'
		| 'focusAreaUnder'
		| 'owner'
		| 'label'
		| 'pageStatus'
		| 'lastUpdatedBy'
		| 'statusFilter'
		| 'compassArchived'
		| 'assetsSchema'
		| 'assetsType'
		| 'watchedByMe'
		| 'positionStatus'
		| 'positionEmploymentType'
		| 'positionJobFamily'
		| 'positionTeam'
		| 'positionFocusArea'
		| 'sort'
		| 'salesforceStage'
		| 'pullRequestStatus'
		| 'campaignStatus'
		| 'caseStatus'
	)[];
} = (
	staticFilters: Record<string, MinimalFilterValue> | MinimalFilterValue[],
	filterStates: Record<string, AllFilterStates | undefined> | AllFilterStates[],
) => {
	const staticFilterValues = Array.isArray(staticFilters)
		? staticFilters
		: Object.values(staticFilters);
	const staticFiltersRecord = Array.isArray(staticFilters)
		? Object.fromEntries(staticFilters.map((staticFilter) => [staticFilter.id, staticFilter]))
		: staticFilters;

	const filterStateValues = Array.isArray(filterStates)
		? filterStates
		: Object.values(filterStates).filter(notEmpty);
	const filterStatesRecord = Array.isArray(filterStates)
		? Object.fromEntries(filterStates.map((filterState) => [filterState.id, filterState]))
		: filterStates;

	return {
		staticFilters: staticFiltersRecord,
		staticFilterValues: staticFilterValues,
		filterStates: filterStatesRecord,
		filterStateValues: filterStateValues,
		getSelectedProducts: (queryParams: QueryParams): string[] => {
			const productKey = productKeySchema.safeParse(queryParams.product);

			if (productKey.success) {
				return [productKey.data];
			}

			const allProductsWithSelections = staticFilterValues
				.filter((staticFilter) => !staticFilter.universal) // Ignore universal filters as they are relevant to every product
				.filter((staticFilter) => queryParams[staticFilter.queryParams.key] !== undefined)
				.map((staticFilter) => staticFilter.products)
				.flat();

			const selectedProducts = Array.from(new Set(allProductsWithSelections)); // might have to sort this based on some priority

			return selectedProducts;
		},
		isMissingProductSelection: (queryParams: QueryParams): boolean => {
			const selectedProducts = FilterUtils(staticFilters, filterStates).getSelectedProducts(
				queryParams,
			);

			const productKey = productKeySchema.safeParse(queryParams.product).data;
			return selectedProducts.length > 0 && !productKey;
		},
		filtersByProduct: (
			products: ProductKeys[],
			experience: SearchStoreExperience,
			primaryProduct?: PrimaryProductKey,
		) => {
			const staticFiltersByProduct = staticFilterValues.filter((staticFilterValue) => {
				if (!products.length && experience === SearchExperience.SEARCH_PAGE) {
					return (
						// If no apps are selected, only universal and suggested filters are relevant
						staticFilterValue.universal || staticFilterValue.isSuggestedFilter?.(primaryProduct)
					);
				}

				if (!products.length && experience === SearchExperience.QUICK_FIND) {
					return staticFilterValue.products.find((product) => product === primaryProduct);
				}

				if (!products.length) {
					return (
						// If no apps are selected, only universal and suggested filters are relevant
						staticFilterValue.universal || staticFilterValue.isSuggestedFilter?.(primaryProduct)
					);
				}

				// Universal filters should always be included for forge connectors
				// This is needed because forge connectors are dynamically loaded and not in the static products list
				if (staticFilterValue.universal && products.some(isForgeConnectorProductKey)) {
					return true;
				}

				return staticFilterValue.products.some((product) => products.includes(product));
			});

			const staticFiltersByProductKeys = staticFiltersByProduct.map(
				(staticFilter) => staticFilter.id,
			);
			const filterStatesByProduct = filterStateValues.filter((filterState) =>
				staticFiltersByProductKeys.includes(filterState.id),
			);

			return FilterUtils(staticFiltersByProduct, filterStatesByProduct);
		},
		getFiltersWithSelection: () => {
			const filterIDsWithSelection = filterStateValues
				.filter((x) => x.hasSelection)
				.map((x) => x.id);
			return filterIDsWithSelection;
		},
	};
};
